import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  AddIntakeJournalPayload,
  SaveIntakeJournalDraftPayload,
  FilterByQuery,
  IncludeQuery,
  Intake,
  IntakeIdentifier,
  IntakeJournal,
  IntakeJournalIdentifier,
  OpenJournalAppointment,
  PaginationQuery,
  PatientIdentifier,
  Questionnaire,
  Response,
  SortByQuery,
  SubmitIntakePayload,
  UpdateIntakePayload,
} from "~/types/api";
import type { Complaint } from "~/types/complaint";
import type { QuestionnaireSchema, QuestionnaireSchemaName } from "~/types/questionnaire";

export function createIntakeApi({
  getRequest,
  postRequest,
  putRequest,
  patchRequest,
  getPaginationRequest,
}: ApiRequestHelpers) {
  /** Fetches a single intake by ID. */
  const intake = (intakeId: IntakeIdentifier) => getRequest<Response<Intake>>(`/intakes/${intakeId}`);

  /** Creates a new intake for a patient, optionally linked to an appointment. */
  const createIntake = (patientId: PatientIdentifier, appointmentId?: number) =>
    postRequest<Response<Intake>>("/intakes/", {
      patient_id: patientId,
      ...(appointmentId && { appointment_id: appointmentId }),
    });

  /** Fetches a specific journal entry for an intake. */
  const intakeJournal = (intakeId: IntakeIdentifier, journalId: IntakeJournalIdentifier) =>
    getRequest<Response<IntakeJournal>>(`/intakes/${intakeId}/journals/${journalId}`);

  /** Fetches all journal entries for an intake. */
  const intakeJournals = (intakeId: IntakeIdentifier) =>
    getRequest<Response<IntakeJournal[]>>(`/intakes/${intakeId}/journals`);

  /** Fetches a paginated list of all intake journals across intakes. */
  const fetchAllIntakeJournals = (
    pagination: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
  ) => getPaginationRequest<IntakeJournal>("/intake-journals", pagination, sortBy, includes, filterBy);

  /** Fetches appointments that do not yet have a journal attached. */
  const fetchOpenJournals = () => getRequest<{ data: OpenJournalAppointment[] }>("/appointments/without-journals");

  /** Fetches all questionnaires associated with an intake. */
  const intakeQuestionnaires = (intakeId: IntakeIdentifier) =>
    getRequest<Response<Questionnaire[]>>(`/intakes/${intakeId}/questionnaires`);

  /** Sends an intake request notification to the patient. */
  const sendIntakeRequest = (intakeId: IntakeIdentifier) =>
    postRequest<Response<Intake>>(`/intakes/${intakeId}/send-request`);

  /** Fetches diagnosis suggestions for an intake, optionally filtered by complaint. */
  const intakeDiagnosis = (intakeId: IntakeIdentifier, complaint?: Complaint) => {
    let endpoint = `/intakes/${intakeId}/diagnosis`;

    if (complaint !== undefined) {
      const params = new URLSearchParams({
        complaint: complaint,
      });

      endpoint += "?" + params.toString();
    }

    return getRequest<Record<string, string>>(endpoint);
  };

  /** Submits a completed intake with all required data. */
  const submitIntake = (intake: IntakeIdentifier, data: SubmitIntakePayload) =>
    putRequest<Response<Intake>>(`/intakes/${intake}`, data);

  /** Partially updates an existing intake. */
  const updateIntake = (intake: IntakeIdentifier, data: UpdateIntakePayload) =>
    patchRequest<Response<Intake>>(`/intakes/${intake}`, data);

  /** Adds a new journal entry to an intake. */
  const addIntakeJournal = (intakeId: IntakeIdentifier, data: AddIntakeJournalPayload) =>
    postRequest<Response<IntakeJournal>>(`/intakes/${intakeId}/journals`, data);

  /** Saves intake remarks without submitting the journal. */
  const saveIntakeJournalDraft = (intakeId: IntakeIdentifier, data: SaveIntakeJournalDraftPayload) =>
    postRequest<Response<IntakeJournal>>(`/intakes/${intakeId}/journals/draft`, data);

  /** Fetches a questionnaire schema definition by name and optional version. */
  const questionnaireSchema = (schema: QuestionnaireSchemaName, version?: number) => {
    const endpoint = version !== undefined ? `/questionnaires/${schema}/${version}` : `/questionnaires/${schema}`;
    return getRequest<QuestionnaireSchema>(endpoint);
  };

  return {
    intake,
    createIntake,
    intakeJournal,
    intakeJournals,
    fetchAllIntakeJournals,
    fetchOpenJournals,
    intakeQuestionnaires,
    sendIntakeRequest,
    intakeDiagnosis,
    submitIntake,
    updateIntake,
    addIntakeJournal,
    saveIntakeJournalDraft,
    questionnaireSchema,
  };
}

export type IntakeApi = ReturnType<typeof createIntakeApi>;
